import type { MouseEvent } from 'react';
import humanizeString from 'humanize-string';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';

import { safeInvoke } from './safe-invoke';
import type { SafeResponse } from './types';

export function getFileExtension(filename: string) {
  return filename.split('.').pop() || '';
}

/**
 * Builds an RFC 6266 `Content-Disposition` header value for a file.
 *
 * Media types (image/video/audio and PDF) use `inline` so the browser previews them;
 * everything else uses `attachment`. Emits both a sanitized ASCII `filename` and an
 * RFC 5987 `filename*=UTF-8''` variant so non-ASCII names survive.
 *
 * @param file - The file whose name and MIME type determine the disposition.
 * @returns The `Content-Disposition` header value.
 */
export function getContentDisposition(file: File) {
  const safeAsciiName = file.name.replace(/[^\w.-]/g, '_');
  const utf8EncodedName = encodeURIComponent(file.name);

  const fileType = file.type.toLowerCase();

  const isMediaFile =
    ['image/', 'video/', 'audio/'].some((type) => fileType.startsWith(type)) || fileType === 'application/pdf';

  const dispositionType = isMediaFile ? 'inline' : 'attachment';

  return `${dispositionType}; filename="${safeAsciiName}"; filename*=UTF-8''${utf8EncodedName}`;
}

export function stopPropagation(event: MouseEvent) {
  event.stopPropagation();
}

export function upsertItems<T>(existingItems: T[], incomingItems: T[], identifier: ((item: T) => any) | keyof T): T[] {
  if (existingItems.length === 0) return incomingItems;
  if (incomingItems.length === 0) return existingItems;

  const items = [...existingItems];
  const getIdentifier = typeof identifier === 'function' ? identifier : (item: T) => get(item, identifier);

  for (const item of incomingItems) {
    const itemId = getIdentifier(item);
    const index = items.findIndex((i) => isEqual(getIdentifier(i), itemId));
    if (index === -1) {
      items.push(item);
    } else {
      items[index] = item;
    }
  }
  return items;
}

export function isPresent<T>(value: T | null | undefined): value is T {
  return value !== null && value !== undefined;
}

// Domain acronyms that should render fully uppercased. humanizeName lowercases
// everything first, so without this "bw" would title-case to "Bw" instead of "BW".
const HUMANIZE_ACRONYMS = new Set(['BW', 'SAP', 'BTP']);

export function humanizeName(
  name: string,
  options?: { extraAcronyms?: readonly string[]; overrides?: Record<string, string> },
): string {
  const acronyms = options?.extraAcronyms?.length
    ? new Set([...HUMANIZE_ACRONYMS, ...options.extraAcronyms.map((a) => a.toUpperCase())])
    : HUMANIZE_ACRONYMS;
  return humanizeString(name)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase())
    .replace(/\b\w+\b/g, (word) => {
      const override = options?.overrides?.[word.toLowerCase()];
      if (override) return override;
      return acronyms.has(word.toUpperCase()) ? word.toUpperCase() : word;
    });
}

export function isLocalDevelopment() {
  return import.meta.env.DEV;
}

/**
 * Normalizes line endings by removing carriage returns.
 * Converts \r\n (Windows) and \r (old Mac) to \n (Unix).
 */
export function normalizeLineEndings(text: string): string {
  return text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
}

export function randomizeArray<T>(array: T[]): T[] {
  const result = [...array];
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

type WithRetryOptions<T, E = Error> = {
  retries: number;
  delay: number;
  shouldRetry?: (error: E) => boolean;
  shouldRetryOnSuccess?: (data: T) => boolean;
};

/**
 * Retries a function with exponential backoff and optional success validation.
 * Returns a SafeResponse object instead of throwing errors.
 *
 * @param fn - The async function to retry
 * @param options - Configuration for retry behavior
 * @returns SafeResponse with either data or error
 *
 * @example
 * // Retry on error
 * const result = await withRetry(() => fetchData(), { retries: 2 });
 * if (result.error) console.error(result.error);
 *
 * @example
 * // Retry on error OR when success doesn't meet criteria
 * const result = await withRetry(
 *   () => queryClient.fetchQuery(options),
 *   {
 *     retries: 2,
 *     shouldRetryOnSuccess: (data) => !data?.program?.code
 *   }
 * );
 */
export async function withRetry<T, E = Error>(
  fn: () => Promise<T>,
  { retries = 3, delay: baseDelay = 500, shouldRetry, shouldRetryOnSuccess }: WithRetryOptions<T, E> = {
    retries: 3,
    delay: 500,
  },
): Promise<SafeResponse<T, E>> {
  let lastResult: SafeResponse<T, E> = { data: null, error: 'No attempts made' as E };

  for (let attempt = 0; attempt <= retries; attempt++) {
    const result = await safeInvoke<T, E>(fn);
    lastResult = result;

    const isLastAttempt = attempt >= retries;
    let shouldRetryThisAttempt = false;

    if (result.data) {
      shouldRetryThisAttempt = !isLastAttempt && shouldRetryOnSuccess?.(result.data) === true;
    } else if (result.error !== null) {
      const errorIsRetryable = shouldRetry?.(result.error) ?? true;
      shouldRetryThisAttempt = !isLastAttempt && errorIsRetryable;
    }

    if (!shouldRetryThisAttempt) {
      return result;
    }

    const delay = baseDelay * 2 ** attempt + Math.random() * 200;
    await new Promise((resolve) => setTimeout(resolve, delay));
  }

  return lastResult;
}
